import axios from "axios";
import { baseUrl } from "../config/apiConfig";
import { refreshAccessToken } from "../store/slices/userSlice";

// Store referansını sonradan enjekte edebilmek için
let reduxStore = null;
let logoutAndResetFn = null;

export const injectStore = (store, { logoutAndReset } = {}) => {
  reduxStore = store;
  logoutAndResetFn = logoutAndReset;
};

// Axios instance oluştur
const api = axios.create({
  baseURL: baseUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

// Refresh token işlemi için flag ve kuyruk
let isRefreshing = false;
let failedQueue = [];

// Kuyruktaki request'leri işle
const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Request interceptor - Her istekte Authorization header'ını ekle
api.interceptors.request.use(
  (config) => {
    // Store henüz enjekte edilmediyse normal config'i döndür
    if (!reduxStore) {
      return config;
    }

    const state = reduxStore.getState();
    const accessToken = state.user.accessToken;

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - 401 hatalarını handle et ve refresh token mekanizması
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Store henüz yoksa, normal şekilde hatayı döndür
    if (!reduxStore) {
      return Promise.reject(error);
    }

    // 401 hatası ve daha önce retry edilmemişse
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Eğer refresh token endpoint'i ise, kuyruğa ekleme
      if (originalRequest.url && originalRequest.url.includes("/api/auth/refresh")) {
        // Refresh token başarısız, logout yap
        logoutAndResetFn?.();
        processQueue(error, null);
        // Kullanıcıyı login sayfasına yönlendir
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }

      // Eğer zaten refresh işlemi devam ediyorsa, request'i kuyruğa ekle
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const state = reduxStore.getState();
      const refreshToken = state.user.refreshToken;

      if (!refreshToken) {
        // Refresh token yoksa logout yap
        logoutAndResetFn?.();
        processQueue(error, null);
        isRefreshing = false;
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }

      try {
        // Refresh token ile yeni access token al
        const result = await reduxStore.dispatch(refreshAccessToken(refreshToken));

        if (refreshAccessToken.fulfilled.match(result)) {
          const newAccessToken = result.payload?.data?.accessToken;
          
          if (newAccessToken) {
            // Yeni token ile original request'i tekrar dene
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            
            // Kuyruktaki tüm request'leri yeni token ile çöz
            processQueue(null, newAccessToken);
            isRefreshing = false;

            return api(originalRequest);
          } else {
            throw new Error("Yeni access token alınamadı");
          }
        } else {
          throw new Error("Refresh token işlemi başarısız");
        }
      } catch (refreshError) {
        // Refresh token başarısız, logout yap
        processQueue(refreshError, null);
        isRefreshing = false;
        logoutAndResetFn?.();
        
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
        
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
